import DOMPurify from "dompurify"

/**
 * Sanitizes the raw email HTML content and provides multiple rendering options
 * using a combination of DOMPurify and iframe sandboxing.
 */
export function sanitizeEmailHTML(html: string): {
  sanitizedContent: string | null
  prunedContent: string | null
  iframeSrc: string | null
} {
  if (!html)
    return {sanitizedContent: null, prunedContent: null, iframeSrc: null}

  // Extract body content
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, "text/html")
  const bodyContent = doc.body ? doc.body.innerHTML : ""

  // Basic sanitization with DOMPurify's default secure configuration
  // This is safer than trying to manually configure every security option
  const sanitizedContent = DOMPurify.sanitize(bodyContent)

  // Text-only version for previews
  const prunedContent = DOMPurify.sanitize(bodyContent, {
    ALLOWED_TAGS: ["div"],
    ALLOWED_ATTR: [],
  })

  // Create blob URL for iframe option - maximum security
  let iframeSrc = null
  try {
    // Create a full HTML document with strict CSP
    const safeDocument = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta http-equiv="Content-Security-Policy" 
                content="default-src 'none'; style-src 'unsafe-inline'; img-src * data: https:; font-src *;">
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, sans-serif; 
              margin: 0; 
              padding: 16px;
              color: #333;
              line-height: 1.5;
            }
            a { color: #2563eb; text-decoration: none; }
            a:hover { text-decoration: underline; }
            img { max-width: 100%; height: auto; }
            pre, code { 
              background-color: #f6f8fa; 
              border-radius: 3px;
              padding: 0.2em 0.4em;
              font-family: monospace;
            }
            table {
              border-collapse: collapse;
              width: 100%;
            }
            td, th {
              border: 1px solid #ddd;
              padding: 8px;
            }
            blockquote {
              border-left: 3px solid #e5e7eb;
              padding-left: 1rem;
              color: #6b7280;
              margin-left: 0;
              margin-right: 0;
            }
          </style>
        </head>
        <body>${sanitizedContent}</body>
      </html>
    `
    const blob = new Blob([safeDocument], {type: "text/html"})
    iframeSrc = URL.createObjectURL(blob)
  } catch (e) {
    console.error("Failed to create iframe source:", e)
  }

  return {sanitizedContent, prunedContent, iframeSrc}
}

/**
 * Renders email content in an iframe with proper sandboxing for maximum security
 * This is the recommended approach for displaying HTML emails
 */
export function createSandboxedIframe(iframeSrc: string | null) {
  if (!iframeSrc) return null

  return (
    <iframe
      src={iframeSrc}
      sandbox="allow-same-origin"
      className="h-full min-h-[200px] w-full border-0"
      title="Email content"
      onLoad={(e) => {
        // Adjust iframe height to content
        if (e.currentTarget.contentWindow) {
          const height =
            e.currentTarget.contentWindow.document.body.scrollHeight
          e.currentTarget.style.height = `${height + 20}px`
        }
      }}
    />
  )
}
